/**
 * WifiControllersList — Controladores WiFi (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`), modal de borrado propio, badges con hex a
 * mano y la ficha abriendo **directamente en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** (con botón Editar) y el borrado usa el diálogo único del
 * CMDB.
 *
 * Botones propios:
 *  · **Ver sus APs** — salta a Puntos de acceso con ese controlador ya filtrado
 *    (`?controllerId=`, que la pantalla de APs ahora sí lee).
 *  · **Ver dónde está montado** — a la ficha del elemento de rack, usando
 *    `docElementPath()` (la convención `?destacar=` del CMDB).
 *  · **Ver en la topología** — al canvas de WiFi.
 *  · **Copiar** — alta precargada con todo menos IP de gestión, serie y asset.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SignalIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ServerStackIcon,
  RectangleGroupIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import WifiControllerForm from './WifiControllerForm';
import { useListView } from '../views/ViewsContext';
import { docElementPath } from '../utils/docElements';
import { getServerFormStyle } from '../utils/serverFormStyle';
import useNavOrder from '../hooks/useNavOrder';
import api from '../services/api';

const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  planned:     { label: 'Planificado',   badge: 'list-badge' },
  decom:       { label: 'Decom',         badge: 'list-badge--off' },
};

const CTRL_TYPE_LABEL = {
  hardware_appliance: 'Appliance físico',
  virtual_appliance:  'VM / Docker',
  cloud:              'Cloud',
  embedded:           'Embebido',
  none:               'Standalone',
};

export default function WifiControllersList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [controllers, setControllers] = useState([]);
  const estiloFicha = getServerFormStyle();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLicense, setFilterLicense] = useState('');
  const [hlId, setHlId] = useState(null);

  const [selected, setSelected] = useState(null);   // null = lista
  const [initialMode, setInitialMode] = useState('edit');
  const [cloneSource, setCloneSource] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getWifiControllers(companyId);
      setControllers(res.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los controladores');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] — convención del CMDB */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !controllers.length) return undefined;
    const id = Number(raw);
    const target = controllers.find(c => c.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`wifi-ctrl-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, controllers, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView = (ctrl) => { setCloneSource(null); setInitialMode('view'); setSelected(ctrl); };
  const openEdit = (ctrl) => { setCloneSource(null); setInitialMode('edit'); setSelected(ctrl); };
  const openCreate = () => { setCloneSource(null); setInitialMode('create'); setSelected({}); };

  /** Copiar: precarga todo menos lo que no puede repetirse (IP, serie, asset). */
  const openCopy = (ctrl) => {
    if (!ctrl) return;
    setSelected(null);
    setCloneSource({
      ...ctrl,
      id: undefined,
      name: ctrl.name ? `${ctrl.name} (copia)` : '',
      hostname: '',
      serial_number: '',
      asset_tag: '',
      management_ip: '',
    });
    setInitialMode('create');
  };

  const closeForm = () => { setSelected(null); setCloneSource(null); load(); };

  const handleDelete = async (ctrl) => {
    try {
      await api.deleteWifiController(ctrl.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando el controlador');
    }
  };

  /* ── filtrado ────────────────────────────────────────────────────────────── */
  const vendorOptions = useMemo(
    () => [...new Set(controllers.map(c => c.vendor || c.vendor_label).filter(Boolean))].sort(),
    [controllers],
  );
  const typeOptions = useMemo(
    () => [...new Set(controllers.map(c => c.controller_type).filter(Boolean))],
    [controllers],
  );

  const licensePct = (c) => {
    const max = c.max_aps || 0;
    if (!max) return null;
    return Math.round(((c.ap_count || 0) / max) * 100);
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return controllers.filter((c) => {
      if (filterStatus && (c.status || 'active') !== filterStatus) return false;
      if (filterVendor && (c.vendor || c.vendor_label) !== filterVendor) return false;
      if (filterType && c.controller_type !== filterType) return false;
      const pct = licensePct(c);
      if (filterLicense === 'high' && !(pct != null && pct > 80)) return false;
      if (filterLicense === 'with' && pct == null) return false;
      if (!needle) return true;
      return (c.name || '').toLowerCase().includes(needle)
        || (c.hostname || '').toLowerCase().includes(needle)
        || (c.model || '').toLowerCase().includes(needle)
        || (c.vendor_label || c.vendor || '').toLowerCase().includes(needle)
        || (c.management_ip || '').toLowerCase().includes(needle)
        || (c.rack_element_name || '').toLowerCase().includes(needle);
    });
  }, [controllers, search, filterStatus, filterVendor, filterType, filterLicense]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navControllers = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('wifi.name', 'Nombre'),
        sortable: true,
        render: (c) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{c.name}</span>
            {c.hostname && <span className="list-hint list-code">{c.hostname}</span>}
          </span>
        ),
      },
      {
        key: 'mounted',
        label: tr('wifi.mounted_at', 'Dónde está'),
        sortable: true,
        sortValue: (c) => c.rack_element_name || '',
        render: (c) => {
          if (!c.rack_element_name && !c.rack_name) return dash;
          const bits = [c.rack_element_name, c.rack_name, c.rack_position_u != null ? `U${c.rack_position_u}` : null]
            .filter(Boolean);
          return <span className="list-hint" title={bits.join(' · ')}>{bits.join(' · ')}</span>;
        },
      },
      {
        key: 'vendor',
        label: tr('wifi.vendor_model', 'Fabricante / modelo'),
        sortable: true,
        sortValue: (c) => `${c.vendor_label || c.vendor || ''} ${c.model || ''}`.trim(),
        render: (c) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{c.vendor_label || c.vendor || dash}</span>
            <span className="list-hint">{c.model || ''}</span>
          </span>
        ),
      },
      {
        key: 'controller_type',
        label: tr('wifi.ctrl_type', 'Tipo'),
        sortable: true,
        sortValue: (c) => c.controller_type || '',
        render: (c) => (
          <span className="list-badge">
            {c.controller_type_label || CTRL_TYPE_LABEL[c.controller_type] || c.controller_type || dash}
          </span>
        ),
      },
      {
        key: 'sites_label',
        label: tr('wifi.sites', 'Sites'),
        sortable: true,
        sortValue: (c) => c.sites_label || '',
        render: (c) => (c.sites_label
          ? <span className="list-code">{c.sites_label}</span>
          : dash),
      },
      {
        key: 'managed_domains',
        label: tr('wifi.domains', 'Dominios de gestión'),
        render: (c) => (c.managed_domains_label
          ? <span className="list-code">{c.managed_domains_label}</span>
          : dash),
      },
      {
        key: 'management_ip',
        label: tr('wifi.mgmt_ip', 'IP de gestión'),
        sortable: true,
        render: (c) => (c.management_ip ? <span className="list-code">{c.management_ip}</span> : dash),
      },
      {
        key: 'ap_count',
        label: tr('wifi.aps', 'APs'),
        sortable: true,
        sortValue: (c) => c.ap_count ?? 0,
        render: (c) => {
          const count = c.ap_count || 0;
          const max = c.max_aps || 0;
          const pct = licensePct(c);
          const tone = pct == null ? 'var(--text-muted)' : pct > 90 ? 'var(--red-600)' : pct > 80 ? 'var(--amber-600)' : 'var(--green-600)';
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title={max ? `${count} de ${max} APs (${pct} % de la licencia)` : `${count} APs`}>
              <button
                type="button"
                className="list-badge"
                style={{ cursor: count ? 'pointer' : 'default' }}
                onClick={(event) => {
                  if (!count) return;
                  event.stopPropagation();
                  navigate(`/wifi/aps?controllerId=${c.id}`);
                }}
                title={tr('wifi.view_aps', 'Ver los APs de este controlador')}
              >
                {count}{max ? ` / ${max}` : ''}
              </button>
              {pct != null && (
                <span className="list-hint" style={{ color: tone, fontWeight: 600 }}>{pct}%</span>
              )}
            </span>
          );
        },
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (c) => c.status || 'active',
        render: (c) => {
          const meta = STATUS_META[c.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (c) => c.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate]);

  const defaultVisible = ['name', 'sites_label', 'mounted', 'vendor', 'controller_type', 'management_ip', 'ap_count', 'status'];

  const filters = [
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'vendor', label: tr('wifi.vendor', 'Fabricante'), allLabel: tr('wifi.vendor_all', 'Todos los fabricantes'),
      value: filterVendor, onChange: setFilterVendor,
      options: vendorOptions.map(v => ({ value: v, label: v })),
    },
    {
      key: 'type', label: tr('wifi.ctrl_type', 'Tipo'), allLabel: tr('wifi.ctrl_type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType,
      options: typeOptions.map(v => ({ value: v, label: CTRL_TYPE_LABEL[v] || v })),
    },
    {
      key: 'license', label: tr('wifi.license', 'Licencia'), allLabel: tr('wifi.license_all', 'Cualquier licencia'),
      value: filterLicense, onChange: setFilterLicense,
      options: [
        { value: 'high', label: tr('wifi.license_high', 'Con más del 80 % de APs') },
        { value: 'with', label: tr('wifi.license_with', 'Con máximo definido') },
      ],
    },
  ];

  const subtitle = `${controllers.length} ${tr('wifi.controllers_plural', 'controladores')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(c => ({
    'Nombre': c.name || '',
    'Hostname': c.hostname || '',
    'Elemento del rack': c.rack_element_name || '',
    'Rack': c.rack_name || '',
    'Posición (U)': c.rack_position_u ?? '',
    'Fabricante': c.vendor_label || c.vendor || '',
    'Modelo': c.model || '',
    'Dominios gestionados': c.managed_domains_label || '',
    'Tipo': c.controller_type_label || CTRL_TYPE_LABEL[c.controller_type] || c.controller_type || '',
    'Sites': c.sites_label || '',
    'IP de gestión': c.management_ip || '',
    'APs': c.ap_count ?? 0,
    'APs máx.': c.max_aps ?? '',
    'Estado': (STATUS_META[c.status] || STATUS_META.active).label,
  })), [filtered]);

  /** Tarjetas: lo esencial del controlador, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.active;
          const pct = licensePct(c);
          return (
            <div
              key={c.id}
              id={`wifi-ctrl-card-${c.id}`}
              className={`list-card${c.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(c)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(c); }}
              title={`${tr('action.view', 'Ver')} ${c.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{c.name}</div>
                  {c.management_ip && <span className="list-code">{c.management_ip}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span>{c.vendor_label || c.vendor || '—'}{c.model ? ` · ${c.model}` : ''}</span>
                <span className="list-badge">{c.controller_type_label || CTRL_TYPE_LABEL[c.controller_type] || c.controller_type}</span>
                {c.rack_element_name && <span>📍 {c.rack_element_name}</span>}
                {c.sites_label && <span title={tr('wifi.sites', 'Sites')}>🌐 {c.sites_label}</span>}
                <span className="list-badge">{c.ap_count || 0}{c.max_aps ? ` / ${c.max_aps}` : ''} APs</span>
                {pct != null && <span className="list-hint">{pct}% de licencia</span>}
              </div>

              <div className="list-card-actions">{rowActions(c)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const CTRL_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada controlador'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('wifi_controllers', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('wifi.select_company', 'Selecciona una empresa para ver los controladores.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {(selected !== null || cloneSource) && (
        <WifiControllerForm
          controller={cloneSource || (selected && selected.id ? selected : null)}
          companyId={companyId}
          initialMode={cloneSource ? 'create' : initialMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          navItems={navControllers}
          navIndex={!cloneSource && selected && selected.id ? navControllers.findIndex(c => String(c.id) === String(selected.id)) : -1}
          onNavigate={(destino) => { setSelected(destino); }}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: (selected !== null || cloneSource) ? 'none' : undefined }}>
        <ListPage
          icon={SignalIcon}
          title={tr('wifi.controllers_title', 'Controladores de red')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('wifi.new_controller', 'Nuevo controlador')}
          createTitle={tr('wifi.new_controller_hint', 'Controlador WiFi (appliance, VM, cloud o embebido)')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('wifi.ctrl_search', 'Buscar por nombre, hostname, modelo, IP…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="wifi-controllers"
          exportSheetName="Controladores WiFi"

          columns={columns}
          columnStorageKey="cmdb_cols_wifi_controllers"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(c) => c.id}
          rowId={(c) => `wifi-ctrl-row-${c.id}`}
          rowClassName={(c) => (c.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(c) => `${tr('action.view', 'Ver')} ${c.name}`}

          views={CTRL_VIEWS}
          listKey="wifi_controllers"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando controladores…')}
          error={error}

          empty={{
            icon: '📡',
            title: controllers.length === 0
              ? tr('wifi.ctrl_empty', 'No hay controladores')
              : tr('msg.no_results', 'Ningún controlador coincide con los filtros'),
            subtitle: controllers.length === 0
              ? tr('wifi.ctrl_empty_hint', 'Da de alta el controlador y luego asígnale sus puntos de acceso.')
              : undefined,
            action: controllers.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('wifi.new_controller', 'Nuevo controlador')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (c) => {
              const mountedPath = c.rack_element_id
                ? docElementPath(c.rack_element_type || 'rack_element', c.rack_element_id)
                : null;
              return (
                <>
                  {/* Ver sus APs (filtra la pantalla de Puntos de acceso) */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!(c.ap_count > 0)}
                    onClick={() => navigate(`/wifi/aps?controllerId=${c.id}`)}
                    title={tr('wifi.view_aps', 'Ver los APs de este controlador')}
                  >
                    <RectangleGroupIcon />
                  </button>
                  {/* Ver dónde está montado (rack / elemento de rack) */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!mountedPath}
                    onClick={() => mountedPath && navigate(mountedPath)}
                    title={c.rack_element_name
                      ? `${tr('wifi.view_mounted', 'Ver dónde está montado')}: ${c.rack_element_name}`
                      : tr('wifi.not_mounted', 'Este controlador no está montado en un rack')}
                  >
                    <ServerStackIcon />
                  </button>
                  {/* Ver en la topología WiFi */}
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => navigate('/wifi/topology')}
                    title={tr('wifi.view_topology', 'Ver la topología WiFi')}
                  >
                    <ShareIcon />
                  </button>
                </>
              );
            },
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('wifi.ctrl_delete_title', 'Eliminar controlador'),
            deleteMessage: (c) => (
              <>
                {tr('wifi.ctrl_delete_confirm', '¿Seguro que quieres eliminar')} <strong>{c.name}</strong>?{' '}
                {c.ap_count > 0
                  ? tr('wifi.ctrl_delete_used', `Ojo: tiene ${c.ap_count} AP(s) vinculados, que se quedarán sin controlador.`)
                  : tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>
    </>
  );
}
